/**
 * Page frames and navigation.
 *
 * The same sections are reachable from a bottom bar on a phone and a side rail on a wide
 * screen, so the controls stay where each device's users expect them.
 */

import { useEffect, type ReactNode } from 'react';
import { Link, NavLink, useParams } from 'react-router-dom';

import { useStore } from '../data/context.js';
import { useProjectDerived } from '../data/derived.js';
import { useRecordProjectOpen } from '../data/prefetch.js';
import { useSession } from '../auth/session.js';
import { Brandmark } from './Brandmark.js';
import { ConnectionRow } from './ConnectionRow.js';
import { plural } from './format.js';
import {
  CalendarIcon,
  ChartIcon,
  ChevronLeftIcon,
  PeopleIcon,
  RollCallIcon,
  SettingsIcon,
  SignOutIcon,
  type IconProps,
} from './icons.js';
import { IconButton } from './primitives.js';

/** A reference Sunday, used to name weekdays in the viewer's locale. */
const REFERENCE_SUNDAY_UTC = Date.UTC(2024, 0, 7);

/** Milliseconds in a day. */
const DAY_MS = 86_400_000;

/** Renders a project's recurring weekdays as a short phrase such as `Mon, Wed`. */
function summarizeWeekdays(weekdays: readonly number[]): string {
  if (weekdays.length === 0) return 'No fixed weekdays';
  return [...weekdays]
    .sort((left, right) => ((left + 6) % 7) - ((right + 6) % 7))
    .map((weekday) => new Date(REFERENCE_SUNDAY_UTC + weekday * DAY_MS)
      .toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' }))
    .join(', ');
}

/** One destination in the project navigation. */
interface NavItem {
  /** Path relative to the project root; the empty string is the roll call. */
  to: string;
  label: string;
  Icon: (props: IconProps) => ReactNode;
}

const PROJECT_NAV: readonly NavItem[] = [
  { to: '', label: 'Roll call', Icon: RollCallIcon },
  { to: 'calendar', label: 'Calendar', Icon: CalendarIcon },
  { to: 'people', label: 'People', Icon: PeopleIcon },
  { to: 'stats', label: 'Stats', Icon: ChartIcon },
  { to: 'settings', label: 'Settings', Icon: SettingsIcon },
];

/** Classes for a navigation link, highlighted when it is the active section. */
function navLinkClasses(isActive: boolean, layout: 'bar' | 'rail'): string {
  const base = layout === 'bar'
    ? 'flex flex-1 flex-col items-center justify-center gap-1 py-2 text-tiny font-medium'
    : 'flex items-center gap-3 rounded-control px-3 py-2.5 text-base font-medium';
  const state = isActive
    ? 'text-brand-text'
    : 'text-ink-2 hover:text-ink';
  const railFill = layout === 'rail' && isActive ? 'bg-brand-soft' : '';
  return `${base} ${state} ${railFill}`;
}

/** The account block for the desktop rail: address, connection state, and the way out. */
function AccountBlock(): ReactNode {
  const { session, signOut } = useSession();
  return (
    <div className="flex flex-col gap-2">
      <p className="truncate text-tiny text-ink-3" title={session?.email ?? ''}>{session?.email ?? ''}</p>
      <ConnectionRow />
      <button
        type="button"
        onClick={() => void signOut()}
        className="flex items-center gap-2 rounded-control py-2 text-tiny font-semibold text-ink-2 hover:text-ink"
      >
        <SignOutIcon size={16} />
        Sign out
      </button>
    </div>
  );
}

/**
 * Frame for pages outside a project, such as the project list.
 *
 * The heading, the account, the connection state, and the page action each occupy their own
 * band, so nothing collides at the narrowest phone width.
 *
 * @param title heading shown at the top of the page.
 * @param action optional control rendered under the heading.
 */
export function PlainFrame({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}): ReactNode {
  const { session, signOut } = useSession();

  return (
    <div className="mx-auto flex min-h-full w-full max-w-3xl flex-col">
      <header className="px-5 pt-6 pb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <Brandmark size={36} />
            <h1 className="truncate text-headline font-bold tracking-tight">{title}</h1>
          </div>
          <IconButton label="Sign out" onClick={() => void signOut()}>
            <SignOutIcon size={20} />
          </IconButton>
        </div>
        {/* Who you are, and — only when there is something to say — how replication is doing.
            No separator between them: one would dangle whenever the status says nothing. */}
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
          <p className="truncate text-tiny text-ink-3" title={session?.email ?? ''}>
            {session?.email ?? ''}
          </p>
          <ConnectionRow />
        </div>
        {action === undefined ? null : <div className="mt-4">{action}</div>}
      </header>
      <main className="flex-1 pb-12">{children}</main>
    </div>
  );
}

/** Frame for pages inside a project: a header, the section content, and the navigation. */
export function ProjectFrame({
  action,
  children,
}: {
  action?: ReactNode;
  children: ReactNode;
}): ReactNode {
  const { projectId } = useParams();
  const store = useStore();
  const derived = useProjectDerived(projectId);
  const name = derived?.project.name ?? 'Project';
  const rosterSize = derived?.people.length ?? 0;
  const weekdaySummary = derived === null ? '' : summarizeWeekdays(derived.project.weekdays);

  useRecordProjectOpen(projectId);
  // Opening a project is when stale numbers would be most visible, so ask for a round now
  // rather than waiting for the background poll.
  useEffect(() => { store.requestRefresh(); }, [store, projectId]);

  return (
    <div className="min-h-full lg:flex">
      <nav
        aria-label="Project sections"
        className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col gap-1 overflow-y-auto
          border-r border-line px-3 py-6 lg:flex"
      >
        <Link
          to="/"
          className="mb-4 flex items-center gap-2 px-3 text-tiny font-semibold text-ink-2 hover:text-ink"
        >
          <ChevronLeftIcon size={16} />
          All projects
        </Link>
        <div className="mb-4 flex items-start gap-2.5 px-3">
          <Brandmark size={28} />
          <div className="min-w-0">
            <p className="text-title leading-tight font-bold break-words">{name}</p>
            <p className="mt-0.5 text-tiny text-ink-3">
              {rosterSize} {plural(rosterSize, 'person', 'people')}
              {weekdaySummary === '' ? '' : ` · ${weekdaySummary}`}
            </p>
          </div>
        </div>
        {PROJECT_NAV.map((item) => (
          <NavLink
            key={item.label}
            to={item.to === '' ? `/p/${projectId ?? ''}` : `/p/${projectId ?? ''}/${item.to}`}
            end={item.to === ''}
            className={({ isActive }) => navLinkClasses(isActive, 'rail')}
          >
            <item.Icon size={20} />
            {item.label}
          </NavLink>
        ))}
        <div className="mt-auto px-3 pt-6"><AccountBlock /></div>
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex items-center gap-1 border-b border-line bg-page/95 px-2 py-2 backdrop-blur lg:hidden">
          <Link
            to="/"
            aria-label="All projects"
            className="inline-flex h-11 w-11 items-center justify-center rounded-control text-ink-2"
          >
            <ChevronLeftIcon size={22} />
          </Link>
          <div className="min-w-0 flex-1">
            <p className="truncate text-row leading-tight font-semibold">{name}</p>
            {weekdaySummary === '' ? null : (
              <p className="truncate text-tiny text-ink-3">{weekdaySummary}</p>
            )}
          </div>
          {action}
        </header>

        <main className="flex-1 pb-[calc(4.5rem+env(safe-area-inset-bottom))] lg:pt-6 lg:pb-16">
          <div className="mx-auto w-full max-w-4xl">{children}</div>
        </main>

        <nav
          aria-label="Project sections"
          className="fixed inset-x-0 bottom-0 z-20 flex border-t border-line bg-surface
            pb-[env(safe-area-inset-bottom)] lg:hidden"
        >
          {PROJECT_NAV.map((item) => (
            <NavLink
              key={item.label}
              to={item.to === '' ? `/p/${projectId ?? ''}` : `/p/${projectId ?? ''}/${item.to}`}
              end={item.to === ''}
              className={({ isActive }) => navLinkClasses(isActive, 'bar')}
            >
              <item.Icon size={22} />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
